import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Reveal from "@/components/jf/Reveal";
import { useGo } from "@/components/jf/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage({ onBook }) {
  const { id } = useParams();
  const go = useGo();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [wood, setWood] = useState("");

  useEffect(() => {
    setImg(0);
    axios.get(`${API}/products/${id}`).then((r) => {
      setP(r.data);
      setWood(r.data.woods?.[0] || "");
    }).catch(() => {});
  }, [id]);

  if (!p) {
    return (
      <main className="bg-ivory min-h-screen pt-48 px-6 md:px-12">
        <p className="text-ink/50 font-light">Loading the piece…</p>
      </main>
    );
  }

  const gallery = p.gallery?.length ? p.gallery : [p.image];

  return (
    <main data-testid="product-page" className="bg-ivory min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-28">
        <button
          data-testid="product-back-btn"
          onClick={() => go("shop")}
          className="text-oxblood/60 hover:text-brass text-xs tracking-[0.3em] uppercase transition-colors duration-300"
        >
          ← Back to Collection
        </button>

        <div className="mt-10 grid md:grid-cols-2 gap-12 md:gap-20">
          <Reveal>
            <motion.div
              key={img}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="aspect-[4/3] overflow-hidden border border-oxblood/20"
            >
              <img data-testid="product-main-image" src={gallery[img]} alt={p.name} className="w-full h-full object-cover" />
            </motion.div>
            <div className="mt-4 flex gap-4">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setImg(i)}
                  className={`w-24 aspect-[4/3] overflow-hidden border transition-all duration-300 ${
                    i === img ? "border-brass" : "border-oxblood/20 opacity-50 hover:opacity-100"
                  }`}
                >
                  <img src={g} alt={`${p.name} view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <span className="text-oxblood text-xs tracking-[0.45em] uppercase">{p.category}</span>
            <h1 className="mt-5 font-display text-oxblood-ink text-4xl md:text-6xl leading-tight">{p.name}</h1>
            <p data-testid="product-price" className="mt-6 font-display text-oxblood text-3xl">
              ₹{Number(p.price).toLocaleString("en-IN")}
            </p>
            <p className="mt-6 text-ink/65 font-light leading-relaxed text-base max-w-lg">{p.description}</p>

            <div className="mt-10 space-y-5 max-w-lg">
              <div data-testid="product-dimensions" className="flex gap-6 items-baseline border-b border-oxblood/15 pb-4">
                <span className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase w-28 shrink-0">Dimensions</span>
                <span className="font-display text-oxblood-ink text-lg">{p.dimensions}</span>
              </div>
              <div className="flex gap-6 items-baseline border-b border-oxblood/15 pb-4">
                <span className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase w-28 shrink-0">Delivery</span>
                <span className="font-display text-oxblood-ink text-lg">{p.delivery}</span>
              </div>
            </div>

            <div className="mt-10">
              <p className="text-oxblood/60 text-[10px] tracking-[0.3em] uppercase mb-4">Choose your wood</p>
              <div className="flex flex-wrap gap-2" data-testid="product-woods">
                {(p.woods || []).map((w) => (
                  <button
                    key={w}
                    data-testid={`wood-${w.toLowerCase()}`}
                    onClick={() => setWood(w)}
                    className={`px-6 py-3 text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
                      wood === w ? "bg-oxblood text-ivory border-oxblood" : "border-oxblood/30 text-oxblood hover:border-brass"
                    }`}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>

            <button
              data-testid="product-book-btn"
              onClick={() => { onBook(`${p.name}${wood ? ` — ${wood}` : ""}`); go("book"); }}
              className="mt-12 w-full md:w-auto bg-oxblood text-ivory hover:bg-oxblood-ink px-12 py-5 text-xs tracking-[0.3em] uppercase transition-colors duration-400"
            >
              Book This Piece
            </button>
            <p className="mt-5 text-ink/45 text-xs font-light max-w-md leading-relaxed">
              No advance payment. Our team calls within 24 hours to confirm wood, finish,
              dimensions and delivery. Every piece is handcrafted in our own Jaipur factory.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
